import React, { useEffect, useState, useRef } from 'react';
import { isAxiosError } from 'axios';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Box,
    Typography,
    IconButton,
    Slider,
    Checkbox,
    Button,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import "./SongsListPopup.css";

interface ISongInfoDB {
    id: number;
    title: string;
    lyrics: string;
    author: string;
    songNumber: string;
    length: string;
    plik?: string;
    accepted?: string | null;
    zaakceptowane?: string;
}

interface AcceptedSongsPopupProps {
    bandNumber: number;
    bandName: string;
    year?: number;
    open: boolean;
    onClose: () => void;
}

const AcceptedSongsPopup = ({ bandNumber, bandName, year, open, onClose }: AcceptedSongsPopupProps) => {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    const INFO_URL = `/admin/songs/${bandNumber}`;

    const [songs, setSongs] = useState<ISongInfoDB[]>([]);
    const [selectedTitles, setSelectedTitles] = useState<string[]>([]);
    const [activeSong, setActiveSong] = useState<ISongInfoDB | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [currentlyPlaying, setCurrentlyPlaying] = useState<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const blobUrlRef = useRef<string | null>(null);

    useEffect(() => {
        if (!bandNumber || !open) return;

        const controller = new AbortController();

        const fetchSongs = async () => {
            setIsLoading(true);
            try {
                const params = year != null ? { year } : {};
                const response = await axiosPrivate.get(INFO_URL, { signal: controller.signal, params });
                let songsData: ISongInfoDB[] = [];

                if (typeof response.data === 'string') {
                    setSongs([]);
                    setSelectedTitles([]);
                    return;
                }
                if (Array.isArray(response.data)) {
                    songsData = response.data;
                } else if (response.data?.songs) {
                    songsData = response.data.songs;
                }

                setSongs(songsData);
                const accepted = songsData
                    .filter(
                        (s: ISongInfoDB) => (s.accepted ?? (s as ISongInfoDB).zaakceptowane) === 'tak'
                    )
                    .map((s: ISongInfoDB) => s.title);
                setSelectedTitles(accepted);
                if (songsData.length > 0) {
                    setActiveSong(songsData[0]);
                } else {
                    setActiveSong(null);
                }
            } catch (error) {
                if (isAxiosError(error)) {
                    if (error.response?.status === 403) {
                        navigate('/', { state: { from: location }, replace: true });
                    } else if (error.response?.status === 404) {
                        setSongs([]);
                        setSelectedTitles([]);
                    }
                } else {
                    setSongs([]);
                    setSelectedTitles([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchSongs();
        return () => controller.abort();
    }, [bandNumber, year, open]);

    const toggleSongSelected = (title: string) => {
        setSelectedTitles((prev) =>
            prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
        );
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            await axiosPrivate.post(
                `${INFO_URL}/accept`,
                {
                    selectedValues: selectedTitles,
                    ...(year != null && { year }),
                },
                { headers: { 'Content-Type': 'application/json' } }
            );
            onClose();
        } catch (error) {
            if (isAxiosError(error)) {
                if (error.response?.status === 403) {
                    navigate('/', { state: { from: location }, replace: true });
                } else {
                    console.error('Error saving song selection:', error);
                }
            }
        } finally {
            setSaving(false);
        }
    };

    const getAudioUrl = (song: ISongInfoDB): string | null => {
        const fileNumber = song.songNumber ?? (song as { numerPiosenki?: string }).numerPiosenki ?? song.plik;
        if (!fileNumber) return null;
        const base = `/admin/songsFiles/${bandNumber}/${String(fileNumber).replace(/\.mp3$/i, '')}.mp3`;
        return year != null ? `${base}?year=${year}` : base;
    };

    const handlePlayPause = async (song: ISongInfoDB) => {
        if (currentlyPlaying === song.id && audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                audioRef.current.play();
                setIsPlaying(true);
            }
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
            const audioUrl = getAudioUrl(song);
            if (!audioUrl) return;
            try {
                const response = await axiosPrivate.get(audioUrl, { responseType: 'blob' });
                const blobUrl = URL.createObjectURL(response.data);
                blobUrlRef.current = blobUrl;
                const audio = new Audio(blobUrl);
                audioRef.current = audio;
                audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
                audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
                audio.addEventListener('ended', () => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                    setCurrentlyPlaying(null);
                    if (blobUrlRef.current) {
                        URL.revokeObjectURL(blobUrlRef.current);
                        blobUrlRef.current = null;
                    }
                });
                await audio.play();
                setCurrentlyPlaying(song.id);
                setIsPlaying(true);
                setActiveSong(song);
            } catch (err) {
                console.error('Error loading audio:', err);
            }
        }
    };

    const handleStop = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setIsPlaying(false);
        setCurrentTime(0);
        setCurrentlyPlaying(null);
    };

    const handleSeek = (newValue: number | number[]) => {
        const seekTime = typeof newValue === 'number' ? newValue : newValue[0];
        if (audioRef.current) {
            audioRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (!open) handleStop();
    }, [open]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Wybierz piosenki – {bandName}</DialogTitle>
            <DialogContent dividers>
                <div className="songs-popup">
                    <div className="songs-list">
                        {isLoading ? (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    Ładowanie...
                                </Typography>
                            </Box>
                        ) : songs.length === 0 ? (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Brak piosenek dla tego zespołu.
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {songs.map((song) => {
                                    const hasAudio = !!getAudioUrl(song);
                                    const isCurrentlyPlaying = currentlyPlaying === song.id;
                                    const isSelected = selectedTitles.includes(song.title);
                                    return (
                                        <ListItem key={song.id} disablePadding>
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => toggleSongSelected(song.title)}
                                                onClick={(e) => e.stopPropagation()}
                                                size="small"
                                                sx={{ color: '#000000', '&.Mui-checked': { color: '#000000' } }}
                                            />
                                            <ListItemButton
                                                onClick={() => setActiveSong(song)}
                                                selected={activeSong?.id === song.id}
                                                sx={{ flex: 1 }}
                                            >
                                                <ListItemText
                                                    primary={song.title}
                                                    secondary={song.author ? `Autor: ${song.author}` : undefined}
                                                />
                                            </ListItemButton>
                                            {hasAudio && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePlayPause(song);
                                                    }}
                                                    color={isCurrentlyPlaying ? "primary" : "default"}
                                                >
                                                    {isCurrentlyPlaying && isPlaying ? (
                                                        <PauseIcon />
                                                    ) : (
                                                        <PlayArrowIcon />
                                                    )}
                                                </IconButton>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </div>
                    <div className="songs-lyrics">
                        {activeSong ? (
                            <>
                                <h3>{activeSong.title}</h3>
                                {activeSong.author && <p><strong>Autor:</strong> {activeSong.author}</p>}
                                {activeSong.length && <p><strong>Długość:</strong> {activeSong.length}</p>}
                                {getAudioUrl(activeSong) && (
                                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <IconButton onClick={() => handlePlayPause(activeSong)} color="primary" size="large">
                                                {currentlyPlaying === activeSong.id && isPlaying ? (
                                                    <PauseIcon />
                                                ) : (
                                                    <PlayArrowIcon />
                                                )}
                                            </IconButton>
                                            <IconButton onClick={handleStop} size="small">
                                                <StopIcon />
                                            </IconButton>
                                            <Box sx={{ flexGrow: 1, ml: 2, mr: 2 }}>
                                                <Slider
                                                    value={currentTime}
                                                    max={duration || 100}
                                                    onChange={(_, value) => handleSeek(value)}
                                                    disabled={!isPlaying && currentlyPlaying !== activeSong.id}
                                                    sx={{ color: 'primary.main' }}
                                                />
                                            </Box>
                                            <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right' }}>
                                                {formatTime(currentTime)} / {formatTime(duration)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'transparent', borderRadius: 1 }}>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', margin: 0 }}>
                                        {activeSong.lyrics || 'Brak tekstu'}
                                    </pre>
                                </Box>
                            </>
                        ) : (
                            <p>Wybierz piosenkę, aby zobaczyć szczegóły</p>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={onClose} color="inherit">
                    Anuluj
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveChanges}
                    disabled={saving || isLoading}
                    sx={{ backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#fb8c00' } }}
                >
                    {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AcceptedSongsPopup;
